import { z } from 'zod';
import { IsoDateTimeSchema, TenantIdSchema, UuidSchema } from './common';

export const RoleSchema = z.enum(['admin', 'librarian', 'reader', 'integration']);
export type Role = z.infer<typeof RoleSchema>;

export const UserSchema = z.object({
  id: UuidSchema,
  tenantId: TenantIdSchema,
  email: z.string().email(),
  displayName: z.string().min(1).max(120),
  avatarUrl: z.string().url().nullable().optional(),
  roles: z.array(RoleSchema).min(1),
  status: z.enum(['active', 'invited', 'suspended', 'deleted']),
  // `internal`: Lectura-natív fiók, jelszóval bejelentkezik.
  // `external`: LMS-launch JIT provisionált fiók, saját login nincs.
  userType: z.enum(['internal', 'external']).default('internal'),
  /** LMS oldali GUID, csak external user-eknél van érték. */
  externalId: z.string().nullable().optional(),
  createdAt: IsoDateTimeSchema,
  lastLoginAt: IsoDateTimeSchema.nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const CreateUserSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(1).max(120),
  password: z.string().min(12).max(128),
  roles: z.array(RoleSchema).min(1).default(['reader']),
});
export type CreateUserInput = z.infer<typeof CreateUserSchema>;

/**
 * Felhasználó-meghívás (jelszó nélkül; a meghívott e-mailben kap aktiváló linket).
 * Az identity-svc kezeli, az admin-web küldi POST /users/invite-re.
 */
export const InviteUserSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(1).max(120),
  roles: z.array(RoleSchema).min(1).default(['reader']),
  /** Választható: külön üzenet a meghívóhoz */
  message: z.string().max(500).optional(),
});
export type InviteUserInput = z.infer<typeof InviteUserSchema>;

/**
 * Szerepkör-átállítás kérés. Csak `admin` jogosult ezt hívni.
 */
export const UpdateUserRolesSchema = z.object({
  roles: z.array(RoleSchema).min(1),
});
export type UpdateUserRolesInput = z.infer<typeof UpdateUserRolesSchema>;

/**
 * Admin profil-módosítás (partial update). Minden mező opcionális — csak a
 * megadottak frissülnek. Az identity-svc kezeli, az admin-web küldi
 * PATCH /users/{id}-re.
 */
export const UpdateUserSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  email: z.string().email().optional(),
  roles: z.array(RoleSchema).min(1).optional(),
});
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof LoginSchema>;

export const TokenPairSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number().int().positive(),
});
export type TokenPair = z.infer<typeof TokenPairSchema>;

export const JwtClaimsSchema = z.object({
  sub: UuidSchema,
  iss: z.string(),
  aud: z.string(),
  exp: z.number().int(),
  iat: z.number().int(),
  jti: z.string(),
  tenant: TenantIdSchema,
  roles: z.array(RoleSchema),
  email: z.string().email().optional(),
});
export type JwtClaims = z.infer<typeof JwtClaimsSchema>;
